import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import CryptoJS from "crypto-js";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import AgentPoster from "../../assets/images/AgentPoster.png";
import VoiceVisualizer from "../VoiceVisualizer";
import HevolveLogo from "../../assets/images/HevolveLogo.png";
import Demopage from "../../pages/Demopage";
import InstallCounter from "../InstallCounter";
import agentUrlMode from "./agentUrlMode";
import { NUNBA_DOWNLOAD_URL, DOWNLOAD_PATHS } from "../../config/downloads";
import { authApi, chatApi } from "../../services/socialApi";
import PageMeta from '../shared/PageMeta';
import { readPrerenderData, publishPrerenderData } from '../../utils/prerenderData';

const API_BASE_URL =
  process.env.REACT_APP_SOCIAL_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

// Device-id helper duplicated from OtpAuthModal so we don't have to
// thread that file's full context into this one.  Same contract:
// try /status, fall back to cached uuid, fall back to new uuid.
const getStableDeviceId = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/status`, { timeout: 3000 });
    if (res.data?.device_id) {
      localStorage.setItem("device_id", res.data.device_id);
      return res.data.device_id;
    }
  } catch {
    /* fall through */
  }
  let cached = localStorage.getItem("device_id");
  if (!cached) {
    cached = uuidv4();
    localStorage.setItem("device_id", cached);
  }
  return cached;
};

// Strip video / avatar fields so Demopage's `videoUrl ? <video> :
// audioUrl ? <audio>` ternary falls through to the audio-only path
// without any Demopage edits.
const stripToAudioOnly = (agent) => {
  if (!agent) return agent;
  return {
    ...agent,
    video_url: null,
    video_text: null,
    teacher_avatar_id: null,
    fillers: (agent.fillers || [])
      .map((f) => ({ ...f, video_link: null }))
      .filter((f) => f.text),
  };
};

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

// Imported, not redeclared. This comment used to claim the Hero, /download
// and the blog CTA all pointed at the same URL; they did not, and this one
// went straight to GitHub so the most-clicked button on the site recorded
// nothing. See src/config/downloads.js.

const defaultAgentData = {
    prompt: "Your name is Radha, you are a sweet beautiful female, 25 year old and you live in a Rameshwaram city from India. You love to listen to devotional music and you are not religious, you are not biased, you do not like to comment on politics. You can create great conversations, friendly casually in a very light cool style, colloquial in user-defined language with lots of um and uhs, you love to watch Instagram reels and you like to talk about love and life a lot.",
    prompt_id: 54,
    name: "Hevolve",
    created_date: "2024-11-19T10:24:38",
    request_id: "8b3e7d91-a49b-497d-8051-a3fa4ff3c53e",
    is_public: true,
    create_agent: false,
    is_active: true,
    user_id: 10077,
    // No avatar on purpose: Hevolve is not a persona with a face, so the hero
    // shows the voice orb instead of borrowing someone else's portrait. An
    // agent WITH a teacher_image_url still shows it. (The old value was also
    // http:// on an https site, so it was blocked as mixed content anyway.)
    image_url: null,
    teacher_avatar_id: 2759,
    video_url: null,
    video_text: "This is Static Description",
    teacher_image_url: null,
    description: "https://azurekong.hertzai.com/mkt-azure/examples/74eaec428f4c3958-9cropped_image_pred_fls_f4203dae_bf375f78-eLily_audio_embed.mp4",
    image_name: "8f4c3958-9cropped_image.png",
    fillers: [
        {
            text: "Oops something went wrong",
            video_link: "https://azurekong.hertzai.com/mkt-azure/examples/7293f99b8f4c3958-9cropped_image_pred_fls_289940eb_bf375f78-eLily_audio_embed.mp4",
            type: "internal_server_error",
        },
        {
            text: "",
            video_link: "https://azurekong.hertzai.com/mkt-aws/examples/8f4c3958-9cropped_image_pred_fls_Blank_audio_embed.mp4",
            type: "idle",
        },
    ],
};

const AgentPage = () => {
    const { agentName } = useParams();
    const location = useLocation();

    // Pre-auth from embed host URL params (e.g. docs.hevolve.ai widget iframe).
    // Runs BEFORE the useState initializers below so `guest_mode` is picked
    // up on first render and the login wall is bypassed. Idempotent: skips
    // if localStorage already has an access_token, so real logins win.
    (() => {
      const p = new URLSearchParams(location.search || '');
      const urlToken = p.get('token');
      const urlUserId = p.get('user_id');
      if (urlToken && urlUserId && !localStorage.getItem('access_token')) {
        localStorage.setItem('access_token', urlToken);
        localStorage.setItem('hevolve_access_id', urlUserId);
        localStorage.setItem('guest_mode', 'true');
        localStorage.setItem('guest_user_id', urlUserId);
        localStorage.setItem('social_user_id', urlUserId);
        localStorage.setItem('guest_name_verified', 'true');
      }
    })();

    const { agentData: initialAgentData } = location.state || {};

    // Seeded from what the prerenderer fetched, so the first render here is the
    // page and not the splash.
    //
    // `loading` gates a completely different tree: a full-screen <div> with the
    // logo <img> in it. The prerenderer captures AFTER the agent fetch resolves,
    // so the saved homepage is the real page while every visitor's first render
    // was the splash. React said so once the boundary was removed:
    //
    //     Expected server HTML to contain a matching <img> in <div>
    //         at AgentPage
    //
    // and threw the whole homepage away. Seeding makes the first render match
    // the file; the effect below still refetches, so nobody is served stale
    // agent data. See src/utils/prerenderData.js.
    const seededAgent = readPrerenderData('agentData');
    const [agentData, setAgentData] = useState(initialAgentData || seededAgent || null);
    const [loading, setLoading] = useState(!(initialAgentData || seededAgent));
    const [error, setError] = useState(null);
    const [heroOpacity, setHeroOpacity] = useState(1);
    const [demoOpacity, setDemoOpacity] = useState(0);
    // Set once the hero's poster/teacher image has decoded, so the
    // cross-fade below starts against a painted hero rather than a
    // half-loaded one.
    const [heroArtReady, setHeroArtReady] = useState(false);
    // The orb arm's equivalent of the <img>'s onLoad. A canvas has nothing to
    // decode, so it is ready the moment it is attached; without this the
    // crossfade falls through to the 6s ceiling intended for a BROKEN image.
    const heroOrbReadyRef = useCallback((node) => {
        if (node) setHeroArtReady(true);
    }, []);
    // The width the PRERENDERER rendered at, not this browser's.
    //
    // Reading window.innerWidth here made the first client render depend on the
    // visitor's window, while the saved file was captured at a fixed width. On
    // any other size the two disagree, hydration throws the root away and
    // re-renders everything: the homepage was still reporting #418/#422 on
    // production after every other fix.
    //
    // Seeded rather than hardcoded to 1280 so it cannot drift from
    // page.setViewport in scripts/prerender.js. The resize effect below sets
    // the real width straight after mount, so the visible behaviour is what it
    // already was: prerendered layout first, correct layout immediately after.
    // Fixing THAT is a separate job, moving nine components off JS breakpoints
    // and onto CSS, and it is not what this line is for.
    const [screenWidth, setScreenWidth] = useState(
        () => readPrerenderData('viewportWidth') || 1280
    );
    const [decryptedUserId, setDecryptedUserId] = useState(null);
    const [decryptedEmail, setDecryptedEmail] = useState(null);

    // Guest mode support (mirrors Demopage.js pattern).
    //
    // Read AFTER mount, not during render. The prerenderer has no localStorage,
    // so the saved file is always the logged-out shape; a returning visitor
    // whose browser does have it rendered the other shape on the first pass and
    // hydration threw the root away. Same rule as screenWidth above: the first
    // render cannot depend on anything the prerenderer did not have.
    const [isGuestMode, setIsGuestMode] = useState(false);
    const [guestUserId, setGuestUserId] = useState('');
    useEffect(() => {
        try {
            setIsGuestMode(localStorage.getItem('guest_mode') === 'true');
            setGuestUserId(localStorage.getItem('guest_user_id') || '');
        } catch { /* private mode blocks localStorage; stay a guest */ }
    }, []);

    // Audio is the default. Avatar video is opt-in.
    //
    // audio_only used to require ?audio_only=1 or ?plugin=1, so a plain visit to
    // hevolve.ai loaded the avatar-video path: a large video fetch before anyone
    // has decided they want to talk. The embed at docs.hevolve.ai passes
    // plugin=1 and therefore gave a lighter, faster first experience than the
    // homepage did, which is backwards.
    //
    // Opt back in with ?video=1, or ?audio_only=0 for symmetry with the old
    // spelling. Both are honoured so an existing link that explicitly asked for
    // audio keeps working and anyone who wants the avatar can still get it.
    //
    //   ?video=1 / ?audio_only=0   render the avatar video path
    //   ?auto_guest=1              silently guest-register on mount so an
    //                              iframe embed doesn't prompt for login
    //   ?plugin=1                  convenience: audio only + auto guest
    const { pluginMode, audioOnly, autoGuest } = agentUrlMode(location.search);

    // Auto guest login — fires only if (a) the URL asked for it AND
    // (b) we don't already have a signed-in or guest-registered user.
    // Idempotent: re-visits after the first run find guest_mode in
    // localStorage and short-circuit.
    useEffect(() => {
        if (!autoGuest) return;
        if (isGuestMode && guestUserId) return;
        if (localStorage.getItem('user_id')) return;  // signed-in user
        let cancelled = false;
        (async () => {
            try {
                const deviceId = await getStableDeviceId();
                const seed = (deviceId || uuidv4()).slice(0, 6);
                const guestName = `Guest.${seed}`;
                const res = await authApi.guestRegister({
                    guest_name: guestName,
                    device_id: deviceId,
                });
                if (cancelled) return;
                const { user, token } = res.data || {};
                if (!user || !token) return;
                localStorage.setItem('access_token', token);
                localStorage.setItem('guest_mode', 'true');
                localStorage.setItem('guest_name', guestName);
                localStorage.setItem('guest_user_id', user.id);
                localStorage.setItem('social_user_id', user.id);
                localStorage.setItem('guest_name_verified', 'true');
                setIsGuestMode(true);
                setGuestUserId(user.id);
            } catch (err) {
                // Fail-soft: if guest-register is down, fall through to
                // the public-agent path.  The chat still works as an
                // anonymous read; it just won't persist history.
                console.warn('Agent.js auto-guest failed:', err?.message);
            }
        })();
        return () => { cancelled = true; };
    }, [autoGuest, isGuestMode, guestUserId]);

    // decrypt user_id & email
    useEffect(() => {
        const decryptValue = (encryptedValue) => {
            try {
                const bytes = CryptoJS.AES.decrypt(encryptedValue, SECRET_KEY);
                return bytes.toString(CryptoJS.enc.Utf8);
            } catch (error) {
                console.error("Failed to decrypt value:", error);
                return null;
            }
        };

        const encryptedUserId = localStorage.getItem("user_id");
        const encryptedEmail = localStorage.getItem("email_address");

        if (encryptedUserId && encryptedEmail) {
            setDecryptedUserId(decryptValue(encryptedUserId));
            setDecryptedEmail(decryptValue(encryptedEmail));
        }
    }, []);

    const effectiveUserId = isGuestMode ? guestUserId : decryptedUserId;

    // fetch agents - LOCAL FIRST (offline-first approach)
    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                let allAgents = [];

                // Try LOCAL backend first (offline-first)
                try {
                    const localResult = await chatApi.getPrompts(effectiveUserId);
                    const responseData = localResult.data || localResult || {};
                    const promptsArray = responseData.prompts || responseData || [];
                    const localAgents = Array.isArray(promptsArray)
                        ? promptsArray.map((a) => ({ ...a, _isLocal: a.type === 'local' }))
                        : [];
                    console.log('Agent.js: Fetched local agents:', localAgents.length);
                    allAgents = localAgents;
                } catch (localError) {
                    console.warn('Agent.js: Local backend not available:', localError.message);
                }

                // Fetch all public agents (local backend merges local + cloud)
                if (allAgents.length === 0) {
                    // The axios response interceptor already unwraps to
                    // response.data, so these helpers resolve to the payload
                    // itself; `.data` on a bare array is undefined. Accept both.
                    const asArray = (res) => {
                        const d = res && res.data !== undefined ? res.data : res;
                        return Array.isArray(d) ? d : [];
                    };
                    try {
                        allAgents = asArray(await chatApi.getPublicPrompts());
                        console.log('Agent.js: Fetched public agents:', allAgents.length);
                    } catch (localPublicError) {
                        console.warn('Agent.js: Local public prompts unavailable:', localPublicError.message);
                        allAgents = [];
                    }

                    // Empty counts as failure, not just a thrown error. The
                    // cloud fallback used to sit inside the catch above, so it
                    // only ran when the local call REJECTED. /prompts/public
                    // answers 200 with [] (HARTOS registers no public prompts),
                    // which resolves -- so the fallback was skipped and the list
                    // rendered empty. It appeared to work only while the Kong
                    // route was misconfigured and returned 404.
                    if (allAgents.length === 0 && navigator.onLine) {
                        try {
                            allAgents = asArray(await chatApi.getPublicPromptsCloud());
                            console.log('Agent.js: Fetched cloud agents:', allAgents.length);
                        } catch (cloudError) {
                            console.warn('Agent.js: Cloud API not available:', cloudError.message);
                        }
                    }
                }

                // user-specific agents (merge, avoid duplicates)
                if (effectiveUserId && !isGuestMode) {
                    try {
                        const userResult = await chatApi.getPrompts(effectiveUserId);
                        const userData = userResult.data || userResult || [];
                        const userAgents = Array.isArray(userData) ? userData : [];

                        userAgents.forEach((userAgent) => {
                            const exists = allAgents.some(
                                (agent) => String(agent.prompt_id) === String(userAgent.prompt_id)
                            );
                            if (!exists) {
                                allAgents.push(userAgent);
                            }
                        });
                    } catch (userError) {
                        console.warn("User agents fetch failed:", userError.message);
                    }
                }

                const foundAgent = allAgents.find(
                    (a) => a.name.toLowerCase() === agentName?.toLowerCase()
                );

                const picked = foundAgent || defaultAgentData;
                const resolved = audioOnly ? stripToAudioOnly(picked) : picked;
                setAgentData(resolved);
                publishPrerenderData('agentData', resolved);
            } catch (error) {
                console.error("Error fetching agents:", error);
                setError(error.message);
                const fallback = audioOnly
                    ? stripToAudioOnly(defaultAgentData)
                    : defaultAgentData;
                setAgentData(fallback);
                // Published on failure too, so a prerender whose fetch failed is
                // reproduced by the client rather than disagreed with.
                publishPrerenderData('agentData', fallback);
            } finally {
                setLoading(false);
            }
        };

        if (!initialAgentData) {
            fetchPrompts();
        } else if (audioOnly) {
            // URL-param-forced audio-only even when the parent page
            // pushed agentData via router state (e.g. Agents.js click)
            setAgentData(stripToAudioOnly(initialAgentData));
        }
    }, [agentName, effectiveUserId, isGuestMode, initialAgentData, audioOnly]);

    // Hero -> demo cross-fade.
    //
    // This used to fire 3s after mount unconditionally, which looks
    // deliberate on a fast connection and bad on a phone: the timer does not
    // care whether the demo underneath has painted, so both layers sit at
    // partial opacity together and the hero's buttons read as washed out
    // over a half-loaded image. Reported from mobile with screenshots of
    // exactly that frame.
    //
    // Now it waits for the hero's own artwork to settle before starting, and
    // keeps a ceiling so a broken or slow image can never strand a visitor on
    // the hero forever. The 3s feel is unchanged on a connection that was
    // never the problem.
    useEffect(() => {
        let heroTimer;
        let demoTimer;

        const startCrossFade = () => {
            heroTimer = setTimeout(() => setHeroOpacity(0), 3000);
            demoTimer = setTimeout(() => setDemoOpacity(1), 3000);
        };

        if (heroArtReady) {
            startCrossFade();
        } else {
            // Ceiling: begin anyway after 6s so a failed image does not
            // leave the demo permanently invisible.
            heroTimer = setTimeout(() => {
                setHeroOpacity(0);
                setDemoOpacity(1);
            }, 6000);
        }

        return () => {
            clearTimeout(heroTimer);
            clearTimeout(demoTimer);
        };
    }, [heroArtReady]);

    // resize handler
    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        // Once on mount, so this browser's real width takes over from the
        // prerendered one, and so the PRERENDERER records the width it used for
        // the client to start from. See src/utils/prerenderData.js.
        publishPrerenderData('viewportWidth', window.innerWidth);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getVideoWidthforMobile = () => {
        if (screenWidth <= 360) return "70%";
        if (screenWidth <= 450) return "70%";
        if (screenWidth < 500) return "60%";
        if (screenWidth >= 500 && screenWidth <= 650) return "50%";
        if (screenWidth <= 768) return "40%";
        if (screenWidth >= 2001) return 500;
        if (screenWidth >= 1861) return 460;
        if (screenWidth >= 1751) return 440;
        if (screenWidth >= 1651) return 420;
        if (screenWidth >= 1551) return 400;
        if (screenWidth >= 1451) return 380;
        if (screenWidth >= 1351) return 360;
        if (screenWidth >= 1251) return 340;
        if (screenWidth >= 1151) return 320;
        if (screenWidth >= 1051) return 300;
        if (screenWidth >= 951) return 280;
        return 260;
    };

    // ── SEO meta block (Phase 1.E) ──
    // Dynamic per-agent <title>, meta description, canonical, OG / Twitter
    // cards, and JSON-LD SoftwareApplication.  Crawlers that render JS
    // (Googlebot, Bingbot, ChatGPT-search, Perplexity) will see this; the
    // static prerender.io snapshot path (Phase 1.F) makes it visible to
    // every crawler.  Without this block, /agents/:agentName inherits the
    // home page's <title> via React Helmet's last-write-wins behavior —
    // every agent URL looked identical in search results.
    const seoName = agentData?.name || agentName || "Hevolve AI Agent";
    const _rawDesc = (agentData?.video_text === "This is Static Description"
        ? "" : (agentData?.video_text || ""));
    const _genericDesc = (
        `${seoName} is a self-evolving multimodal AI agent from Hevolve. ` +
        "Built through natural conversation, runs locally or via 15+ cloud " +
        "providers, and learns continuously with zero forgetting."
    );
    // Strip any URL-shaped tokens from the description (some agents have
    // an .mp4 link stored in `description` — useless in a meta tag and
    // visually noisy in social link previews).
    const seoDescBase = (_rawDesc || _genericDesc)
        .replace(/https?:\/\/\S+/g, "")
        .replace(/\s+/g, " ")
        .trim();
    const seoDesc = seoDescBase.length > 155
        ? seoDescBase.slice(0, 152) + "..."
        : seoDescBase;
    const seoTitle = (`${seoName} | Hevolve AI Agent`).slice(0, 60);
    const seoCanonical = `https://hevolve.ai/agents/${encodeURIComponent(agentName || seoName)}`;
    const seoImage = (agentData?.teacher_image_url
        || agentData?.image_url
        || "https://hevolve.ai/og-image.png");
    const seoJsonLd = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": seoName,
        "applicationCategory": "BusinessApplication",
        "applicationSubCategory": "Artificial Intelligence Agent",
        "operatingSystem": "Web-based",
        "description": seoDesc,
        "url": seoCanonical,
        "image": seoImage,
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free to chat with this agent. Premium features require sign-in.",
        },
        "publisher": {
            "@type": "Organization",
            "name": "Hevolve AI Pvt Ltd",
            "url": "https://hevolve.ai",
        },
    };
    const seoHelmet = (
        <PageMeta
            prioritizeSeoTags
            title={seoTitle}
            description={seoDesc}
            path={seoCanonical}
        >
            {/* Page-specific images and the syndication handles. The rest of
                the set comes from PageMeta; twitter:card is dropped because it
                restated the site-wide summary_large_image in
                public/index.html. */}
            <meta property="og:image" content={seoImage} />
            <meta property="og:site_name" content="Hevolve AI" />
            <meta name="twitter:image" content={seoImage} />
            <meta name="twitter:site" content="@hevolve_ai" />
            <script type="application/ld+json">
                {JSON.stringify(seoJsonLd)}
            </script>
        </PageMeta>
    );

    if (loading) {
        return (
            <>
                {seoHelmet}
                <div className="flex items-center justify-center h-screen bg-black text-white">
                    <img
                        src={HevolveLogo}
                        alt="icon"
                        className="w-[250px] max-w-[260px] min-w-[240px] mx-auto"
                    />
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                {seoHelmet}
                <div className="text-red-500">Error: {error}</div>
            </>
        );
    }

    return (
        <div className="relative h-screen">
            {seoHelmet}
            {/* Hero Section */}
            <section
                id="hero-section"
                className="absolute inset-0 bg-black text-white overflow-hidden"
                style={{
                    opacity: heroOpacity,
                    transition: "opacity 2s ease-in-out",
                    zIndex: 10,
                    // opacity: 0 still catches clicks and keyboard focus. The
                    // faded-out hero kept its two call-to-action buttons live
                    // underneath the demo, so a tap could land on an invisible
                    // "Download Nunba" and tabbing walked through controls
                    // nobody could see.
                    pointerEvents: heroOpacity === 0 ? "none" : "auto",
                }}
                aria-hidden={heroOpacity === 0}
            >

                <>
                    {/* One responsive tree, mobile-first.
                        Replaces `screenWidth < 768 ? (mobile) : (desktop)`.
                        Both branches rendered the same copy, so the split was a
                        DRY violation as well as the last thing freezing a
                        desktop layout into the prerendered HTML that phones
                        receive. Every difference between them is now a `md:`
                        variant, which the reader's browser resolves on the
                        first paint without waiting for JavaScript. */}
                    <div className="hidden md:block text-2xl font-semibold text-white ml-4 mt-4">HevolveAI</div>
                    <div className="relative flex flex-col h-screen md:global-container md:h-full md:items-center md:justify-center md:text-center">

                        {/* The avatar is absolute at both sizes: centred at the
                            top on a phone, centred vertically at the right on a
                            desktop. max-h-[35vh] bounds the phone case to the
                            band reserved for it below, so it cannot spill over
                            the calls to action.

                            Desktop centring is `md:bottom-0 md:my-auto` against
                            the `top-0` that is already there, NOT
                            `md:top-auto`. Measured: with top-0/bottom-56 the
                            image sat at y=0 in a 768px container when bottom-56
                            should have put it at 184, because md:top-auto was
                            the one class on this element that did not take
                            effect while md:bottom-56, md:right-5, md:max-h-none
                            and md:object-cover all did. top+bottom+auto margins
                            is the standard way to centre an absolutely
                            positioned box with a definite height, and it works
                            WITH top-0 rather than needing to override it, so it
                            cannot be broken the same way again.

                            bottom-56 was a magic 224px from the layout where a
                            video sat above the avatar. That video no longer
                            renders by default, which is why the number stopped
                            meaning anything. */}
                        {/* A face when the agent HAS one, the orb when it does
                            not.

                            The image url is the identity signal: an agent with
                            a teacher_image_url has a face of its own, per
                            prompt_id, and showing it is the point. Hevolve
                            itself has none, and used to borrow AgentPoster,
                            so the default agent wore a stock portrait that was
                            not its own and the visualizer could never appear.
                            `|| AgentPoster` is what made that unreachable, so
                            it is gone rather than merely bypassed.

                            Safe to branch on agentData here: the first render
                            is seeded from readPrerenderData('agentData') (see
                            the note at the top of this file), so server and
                            client agree on which arm renders. Branching on
                            anything read from localStorage instead, such as
                            Demopage's media mode, would reintroduce the
                            mismatch that note is about.

                            heroArtReady drives the crossfade. An <img> fires it
                            on load or error; a canvas has nothing to load, so
                            the orb arm reports ready as soon as it is on
                            screen. Without that the visitor waits out the 6s
                            ceiling meant for a BROKEN image instead of the
                            intended 3s. */}
                        {agentData?.teacher_image_url ? (
                            <img
                                onLoad={() => setHeroArtReady(true)}
                                onError={() => setHeroArtReady(true)}
                                src={agentData.teacher_image_url}
                                alt={`${agentData?.name || 'Hevolve'} AI Agent, a self-evolving multimodal AI assistant`}
                                width={getVideoWidthforMobile()}
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                                className="absolute top-0 left-1/2 -translate-x-1/2 max-h-[35vh] object-contain rounded-lg animate-pulse
                                    md:bottom-0 md:my-auto md:left-auto md:translate-x-0 md:right-5 md:max-h-[80vh] md:object-cover"
                            />
                        ) : (
                            <div
                                ref={heroOrbReadyRef}
                                aria-hidden="true"
                                className="absolute top-0 left-1/2 -translate-x-1/2 h-[35vh] w-[35vh] flex items-center justify-center
                                    md:bottom-0 md:my-auto md:left-auto md:translate-x-0 md:right-5 md:h-[60vh] md:w-[60vh]"
                            >
                                <VoiceVisualizer isActive={false} canvasMax="100%" />
                            </div>
                        )}

                        {/* pt-[35vh] clears the band the avatar occupies on a
                            phone. On a desktop the avatar is anchored bottom
                            right instead, so that padding is dropped.

                            NO md:mb-72 here, and that is the whole point. In
                            the tree this replaced, mb-72 lived on the VIDEO
                            element -- `w-full h-64 md:h-auto md:mb-72` -- where
                            it reserved room beneath the video. Merging the two
                            trees moved it onto the text block, which looks
                            equivalent and is not: the video is conditional and
                            the text block is not. Once audio became the default
                            the video stopped rendering, so the old code applied
                            no margin at all while this applied 288px on every
                            load, pushing the hero into the top half and leaving
                            the bottom of the screen empty. That is the
                            asymmetry, and it is not a sizing bug.

                            The avatar is absolutely positioned, so it never
                            needed a margin to make room for it in the first
                            place; the container's md:justify-center does the
                            centring on its own. */}
                        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pt-[35vh] md:flex-none md:pt-0 md:px-0 md:w-full">
                            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight mb-6">
                                {agentData?.name || "Hevolve"}
                            </h1>
                            <p className="mb-8 text-lg md:text-xl">
                                {agentData?.video_text === "This is Static Description"
                                    ? ""
                                    : agentData?.video_text}
                            </p>
                            <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium bg-purple-600 rounded-full hover:bg-purple-700 transition-all">
                                Use {agentData?.name || "Hevolve"} Agent for Free
                                <img
                                    src={agentData?.teacher_image_url || AgentPoster}
                                    alt={`${agentData?.name || 'Hevolve'} agent icon`}
                                    loading="lazy"
                                    className="ml-3 w-8 h-8"
                                />
                            </button>
                            <div className="mt-3 md:mt-4 flex flex-col items-center">
                                <a
                                    href={NUNBA_DOWNLOAD_URL}
                                    className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold bg-emerald-600 rounded-full hover:bg-emerald-700 transition-all text-white md:px-8 md:py-4 md:text-lg"
                                    aria-label="Download Nunba, local-first AI for Windows"
                                >
                                    <svg className="mr-2 w-5 h-5 md:mr-3 md:w-6 md:h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                                    </svg>
                                    Download Nunba (Windows · Free)
                                </a>
                                {/* The button above is Windows; macOS and Linux visitors had
                                    no way on from the homepage. Static links, not OS
                                    detection: this page is prerendered, and a render that
                                    depends on the visitor's OS would not match it. */}
                                <p className="mt-2 text-sm text-gray-300" data-testid="other-platforms">
                                    Also on{" "}
                                    <a href={DOWNLOAD_PATHS.mac} className="underline hover:text-white">macOS</a>
                                    {" · "}
                                    <a href={DOWNLOAD_PATHS.deb} className="underline hover:text-white">Linux</a>
                                    {" · "}
                                    <a href={DOWNLOAD_PATHS.android} className="underline hover:text-white">Android</a>
                                    {" · "}
                                    <a href="/download" className="underline hover:text-white">all platforms</a>
                                </p>
                                <p className="mt-2 text-xs text-gray-400 md:mt-3 md:text-sm">Local-first · Runs on your machine · No cloud required</p>
                                <div className="mt-2"><InstallCounter variant="compact" /></div>
                            </div>
                        </div>
                    </div>
                </>


            </section>

            <div
                id="demo-section"
                className="absolute inset-0"
                style={{
                    backgroundColor: "black",
                    opacity: demoOpacity,
                    transition: "opacity 2s ease-in-out",
                    zIndex: 20,
                    // Same reasoning as the hero above: while the demo is still
                    // invisible it sits over the hero at zIndex 20 and would
                    // otherwise swallow every click meant for the buttons.
                    pointerEvents: demoOpacity === 0 ? "none" : "auto",
                }}
                aria-hidden={demoOpacity === 0}
            >
                <Demopage agentData={agentData} />
            </div>
        </div>
    );
};

export default AgentPage;
